import rootStore from '@vromo/dashboard/src/stores/RootStore';
import { Job } from '@vromo/models';
import convertDistance from '@vromo/utils/convertDistance';
import areRangesOverlapping from 'date-fns/are_ranges_overlapping';
import { last, sortBy } from 'lodash';
import { ExtendedVehicle } from '../ReportRoot.lazy';

export type Rate = {
    fixed: number;
    perCompletedJob: number;
    perCancelled: number;
    perKmJob: number;
    perKmEmpty: number;
};

export type DriverReportViewData = {
    id: string;
    name: string;
    group: string;
    availableHours: string;
    timeOnJob: string;
    totalTravel: string;
    onJobTravel: string;
    offJobTravel: string;
    avgRating: string;
    payEstimate: string;
    costPerJob: string;
    jobsCompleted: string;
    jobsCancelled: string;
    jobsReoffered: string;
    phone: string;
    email: string;
    external: string;
};

export const calculatePay = (
    vs: ExtendedVehicle,
    rates: Rate
): {
    estimate: number;
    perJob: number;
} => {
    const { viewStore } = rootStore;
    const estimate =
        rates.fixed +
        rates.perCompletedJob * vs.jobsCompleted +
        rates.perCancelled * vs.jobsCancelled +
        rates.perKmJob *
            convertDistance(vs.totalTravel - vs.offJobTravel, viewStore.imperialUnits).distance() +
        rates.perKmEmpty * convertDistance(vs.offJobTravel, viewStore.imperialUnits).distance();
    const perJob = vs.jobsCompleted < 1 ? estimate : estimate / vs.jobsCompleted;
    return { estimate, perJob };
};

export const calculateCostPerJob = (payEstimate: number, jobsCompleted: number): number => {
    if (jobsCompleted < 1) {
        return payEstimate;
    } else {
        return payEstimate / jobsCompleted;
    }
};

export const getDriverData = (
    v: ExtendedVehicle,
    jobs: Job[],
    rates?: Rate
): DriverReportViewData => {
    const { groupStore } = rootStore;
    const timeOnJobMap = calculateTimeOnJob(jobs);
    const timeOnJob = timeOnJobMap.get(v.id!);
    const { viewStore } = rootStore;
    const { totalAvailability, totalTravel, offJobTravel, avgRating, group, driver } = v;
    const { estimate = undefined, perJob = undefined } = rates ? calculatePay(v, rates) : {};

    return {
        id: v.id!.toString(),
        email: driver?.contact?.email || '-',
        name: driver?.contact?.name || driver?.contact?.email || 'Unknown Driver',
        external: v.external?.toString() || '',
        phone: driver?.contact?.phone || 'Unknown Phone',
        jobsCompleted: v.jobsCompleted.toString(),
        jobsCancelled: v.jobsCancelled.toString(),
        jobsReoffered: v.jobsReoffered?.toString() || '0',
        group: groupStore.withId(group!)?.name || 'Unknown Group',
        availableHours: totalAvailability.toFixed(2).toString(),
        timeOnJob: timeOnJob ? (timeOnJob / 3600000).toFixed(2).toString() : '0.00',
        totalTravel: convertDistance(totalTravel, viewStore.imperialUnits).distance().toFixed(2),
        onJobTravel: convertDistance(totalTravel - offJobTravel, viewStore.imperialUnits)
            .distance()
            .toFixed(2),
        offJobTravel: convertDistance(offJobTravel, viewStore.imperialUnits).distance().toFixed(2),
        avgRating: avgRating ? `${(avgRating * 5).toFixed(1)} / 5` : 'N/A',
        payEstimate: estimate?.toFixed(2) ?? '-',
        costPerJob: perJob?.toFixed(2) ?? '-'
    };
};

export const calculateTimeOnJob = (jobs: Job[]): Map<number, number> => {
    const getDriverJobIntervals = (): Map<number, [number, number][]> => {
        const result = new Map();

        jobs.forEach((j) => {
            const underway = j.dispatches!.items.find((d) => d.type === 'underway');
            const closed = j.dispatches!.items.find((d) => d.type === 'closed');

            if (underway != null && closed != null && underway.vehicle!.id === closed.vehicle!.id) {
                result.has(closed.vehicle!.id)
                    ? result.set(closed.vehicle!.id, [
                          ...result.get(closed.vehicle!.id),
                          [underway.created, closed.created]
                      ])
                    : result.set(closed.vehicle!.id, [[underway.created, closed.created]]);
            }
        });

        return result;
    };

    const driverTimeIntervals = getDriverJobIntervals();
    const driverTimeOnJob = getDriverIntervalSum(driverTimeIntervals);

    return driverTimeOnJob;
};

const getDriverIntervalSum = (
    jobIntervalMap: Map<number, [number, number][]>
): Map<number, number> => {
    const result: Map<number, number> = new Map();
    jobIntervalMap.forEach((intervals, vehicleId) => {
        const mergedIntervals = sortBy(intervals, (int) => int[0]).reduce<[number, number][]>(
            (mergedInt, int) => {
                const lastInterval = last(mergedInt);
                lastInterval &&
                areRangesOverlapping(lastInterval[0], lastInterval[1], int[0], int[1])
                    ? (mergedInt[mergedInt.length - 1] = [lastInterval[0], int[1]])
                    : mergedInt.push([int[0], int[1]]);

                return mergedInt;
            },
            []
        );

        const timeOnJob = mergedIntervals.reduce(
            (sum, interval) => sum + (interval[1] - interval[0]),
            0
        );

        result.set(vehicleId, timeOnJob);
    });

    return result;
};
